/**
 * src/state/patchConflicts.js
 *
 * CONC-01 (GH #7817) — what the browser does when state-write refuses a patch
 * because a field it changed was changed to something else in the meantime
 * (409 `field-conflict`).
 *
 * Only a genuine same-field clash reaches here: a change to any other key,
 * record or field by another session lands without a conflict. So:
 *   • every operation of this save that does NOT clash is kept and re-sent
 *     straight away (rebaseOnConflict) — the user loses nothing that did not
 *     collide;
 *   • every one that does is shown to the user with both values, and the
 *     user's own value is kept in the notice, never dropped (describeConflict);
 *   • the user may choose to keep their own value (keepMineOps), which is an
 *     ordinary save marked as a deliberate overwrite and logged server-side.
 *
 * "Keep mine" re-asserts a value from the past over someone else's, so it is
 * offered only where that cannot over-claim (co-owner, TRYGGR_INTENT.md §6.2):
 * never on a status-bearing field (a status, a verification, a completion, an
 * approval, an outcome — those are re-done through the record's own screen,
 * which runs its own rules), and never on a key the server owns.
 *
 * A record someone else DELETED while this user edited it can be restored
 * (GUARD-01 condition 3), but only fail-safe (co-owner, CONC-01 follow-up):
 * the restore brings back the record as the server LAST STORED it, plus this
 * user's non-status field edits. A status change is never re-applied by a
 * restore — it is redone on the record's own screen, where its transition
 * rules run. A record that carried closure, approval, completion or
 * verification evidence is not offered for restore at all, because re-creating
 * it would re-stamp that evidence as if the restorer had produced it today.
 * After the restore the tab re-reads the stored state and says so if the
 * record did not come back as sent.
 */

// STATUS_BEARING_FIELDS is the server's own list, so the notice and the
// merge rule can never disagree about which fields are compliance claims.
import {
  diffState,
  applyPatch,
  valueAt,
  STATUS_BEARING_FIELDS,
  statusOf,
} from '../../azure-functions/shared/state/statePatch.cjs';

/**
 * Rebase this save onto the state that won.
 *
 * @param {object} args
 * @param {object} args.preSave   the server state this save was diffed against
 * @param {object} args.toSave    the state the user was saving
 * @param {object} args.fresh     the state the server returned with the refusal
 * @param {(s: object) => object} args.strip  removes keys the blob does not own
 * @returns {{ rebased: object, conflicts: Array<object> }}
 *   `rebased` is `fresh` with every non-clashing operation applied, plus the
 *   user's in-flight edits to keys owned by their own stores; `conflicts` are
 *   the clashes, as the server would name them.
 */
export function rebaseOnConflict({ preSave, toSave, fresh, strip }) {
  const ops = diffState(strip(preSave || {}), strip(toSave || {}));
  const { state, conflicts } = applyPatch(fresh || {}, ops);
  // Keys the blob does not carry (risk outbox, controls-write, …) are written
  // by their own endpoints. A local edit to one of them that has not settled
  // yet must survive adopting the fresh snapshot.
  const blobKeys = new Set(Object.keys(strip(toSave || {})));
  for (const k of Object.keys(toSave || {})) {
    if (blobKeys.has(k)) continue;
    const changedLocally = JSON.stringify(toSave[k]) !== JSON.stringify((preSave || {})[k]);
    if (changedLocally) state[k] = toSave[k];
  }
  return { rebased: state, conflicts: collapseGone(conflicts, toSave, preSave) };
}

/**
 * One entry per record someone else DELETED while this user was editing it,
 * carrying the record as this user last had it (`userRecord`) so they can
 * restore it with their edit — or let the delete stand. Every other clash is
 * returned unchanged. `userState` is the state the user was saving.
 */
export function collapseGone(conflicts, userState, baseState) {
  const out = [];
  const seen = new Map();
  for (const c of conflicts || []) {
    if (c.kind !== 'gone' || !c.record) {
      out.push(c);
      continue;
    }
    const { collection, idKey, id } = c.record;
    const key = JSON.stringify([collection, idKey, id]);
    if (seen.has(key)) continue;
    const recPath = [...collection, { k: idKey, v: id }];
    const user = valueAt(userState || {}, recPath);
    const base = valueAt(baseState || {}, recPath);
    const entry = {
      kind: 'gone',
      path: recPath,
      record: c.record,
      ...(user.present ? { userRecord: user.value, mine: user.value } : {}),
      ...(base.present ? { baseRecord: base.value } : {}),
    };
    seen.set(key, entry);
    out.push(entry);
  }
  return out;
}

/** Evidence a re-created record would re-stamp as if produced today. */
const EVIDENCE_STAMPS = [
  'closedAt',
  'completedAt',
  'approvedAt',
  'verifiedAt',
  'decidedAt',
  'publishedAt',
  'signedAt',
];
const EVIDENCE_FLAGS = ['approved', 'verified', 'completed', 'effectivenessVerified'];

function carriesEvidence(rec) {
  if (!rec || typeof rec !== 'object') return false;
  return EVIDENCE_STAMPS.some((f) => !!rec[f]) || EVIDENCE_FLAGS.some((f) => rec[f] === true);
}

/**
 * The record a restore sends: as last stored, plus the user's edits to
 * fields that carry no status. Returns null when a fail-safe restore is not
 * possible. `statusNotReapplied` names the status fields the user had changed.
 */
export function restoreRecordFor(conflict) {
  const base = conflict.baseRecord;
  const user = conflict.userRecord;
  if (!base || !user || typeof base !== 'object' || typeof user !== 'object') return null;
  if (carriesEvidence(base)) return null;
  const record = { ...base };
  const statusNotReapplied = [];
  for (const f of new Set([...Object.keys(base), ...Object.keys(user)])) {
    if (JSON.stringify(base[f]) === JSON.stringify(user[f])) continue;
    if (STATUS_BEARING_FIELDS.has(f)) {
      statusNotReapplied.push(f);
      continue;
    }
    if (user[f] === undefined) delete record[f];
    else record[f] = user[f];
  }
  if (carriesEvidence(record)) return null;
  return { record, statusNotReapplied };
}

function recordTitle(rec, id) {
  return (rec && (rec.title || rec.name || rec.label)) || String(id);
}

function segLabel(seg, container) {
  if (typeof seg === 'string') return seg;
  const rec = Array.isArray(container) ? container.find((r) => r && r[seg.k] === seg.v) : null;
  return (rec && (rec.title || rec.name || rec.label)) || String(seg.v);
}

/** A readable trail for a conflict path, using record titles where it can. */
export function conflictLabel(conflict, state) {
  const parts = [];
  let cur = state;
  for (const seg of conflict.path) {
    parts.push(segLabel(seg, cur));
    if (cur == null) continue;
    cur =
      typeof seg === 'string'
        ? cur[seg]
        : Array.isArray(cur)
          ? cur.find((r) => r && r[seg.k] === seg.v)
          : undefined;
  }
  return parts.join(' › ');
}

/**
 * What the notice shows for one clash. `mine` is always carried, so the
 * user's input is never lost from view even where it cannot be re-applied.
 */
export function describeConflict(conflict, freshState, { serverOwnedKeys = [] } = {}) {
  const last = conflict.path[conflict.path.length - 1];
  if (conflict.kind === 'gone' && conflict.record) {
    // GUARD-01 condition 3: the record was deleted while this user edited it.
    // Both acts are real; the user decides, and either choice is recorded.
    const restore = restoreRecordFor(conflict);
    return {
      ...conflict,
      label:
        conflictLabel({ path: conflict.record.collection }, freshState) +
        ` › ${recordTitle(conflict.userRecord, conflict.record.id)}`,
      deleted: true,
      action: 'restore',
      overridable: !!restore,
      ...(restore && restore.statusNotReapplied.length
        ? {
            note:
              `restoring brings back its last saved ${restore.statusNotReapplied.join(', ')} — ` +
              'set your change again on the restored record',
          }
        : {}),
      notOverridableBecause: restore
        ? null
        : carriesEvidence(conflict.baseRecord)
          ? 'it was closed, approved or verified — re-creating it here would re-stamp that evidence; recreate it from its own screen'
          : 'the record was deleted by someone else',
    };
  }
  const reason =
    conflict.kind === 'record-changed'
      ? 'someone else changed this record at the same time — check it before setting this again'
      : conflict.kind !== 'changed'
        ? 'the record was deleted or replaced by someone else'
        : serverOwnedKeys.includes(conflict.path[0])
          ? 'this value is set by Tryggr itself'
          : typeof last === 'string' && STATUS_BEARING_FIELDS.has(last)
            ? 'a status is changed from the record itself, so its own checks run'
            : null;
  return {
    ...conflict,
    label: conflictLabel(conflict, freshState),
    action: conflict.op === 'remove' ? 'delete' : 'keep',
    overridable: reason === null,
    notOverridableBecause: reason,
  };
}

/**
 * The operation that re-applies the user's value over the current one, as a
 * deliberate overwrite. Built against the CURRENT state, so it lands unless
 * the field moves yet again (in which case the user is asked again).
 */
export function keepMineOp(conflict, currentState) {
  if (conflict.kind === 'gone' && conflict.record) {
    // Restore the record as last stored plus the user's non-status edits
    // (restoreRecordFor) — never a status the record's own rules did not see.
    const restore = restoreRecordFor(conflict);
    if (!restore) return null;
    return {
      op: 'insert',
      path: conflict.record.collection,
      idKey: conflict.record.idKey,
      record: restore.record,
      afterId: null,
      atEnd: true,
      override: true,
    };
  }
  if (conflict.op === 'remove') {
    const at = valueAt(currentState, [...conflict.path, { k: conflict.idKey, v: conflict.id }]);
    if (!at.present) return null;
    return {
      op: 'remove',
      path: conflict.path,
      idKey: conflict.idKey,
      id: conflict.id,
      before: at.value,
      override: true,
    };
  }
  if (conflict.op) return null;
  const at = valueAt(currentState, conflict.path);
  const op = { op: 'set', path: conflict.path, override: true };
  if (conflict.path.length >= 2) {
    // Same rule as every in-record change: say what status the record had.
    op.statusBefore = statusOf(valueAt(currentState, conflict.path.slice(0, -1)).value);
  }
  if (at.present) op.before = at.value;
  else op.beforeAbsent = true;
  if (conflict.mine === undefined) op.afterAbsent = true;
  else op.after = conflict.mine;
  return op;
}

/** `state` with a keep-mine operation applied — what the tab shows next. */
export function applyKeepMine(state, op) {
  return applyPatch(state, [op]).state;
}

/**
 * RDRC-04A — three-way merge of one employee record (TRYGGR_INTENT.md §13).
 * The screen holds unsaved edits (`mine`) made on top of `base`, the copy it
 * last had from the server; the server has just committed `theirs` (an
 * employees-write action, carrying anything another user saved meanwhile).
 * Every field only one side changed lands; a field both changed to different
 * values keeps `theirs` on screen and comes back as a conflict naming the
 * field and both values, for the user to keep theirs or keep mine.
 *
 * @returns {{ employee: object, conflicts: Array<object> }}
 */
export function mergeEmployeeEdits({ base, mine, theirs }) {
  if (!base) return { employee: theirs, conflicts: [] };
  const wrap = (e) => ({ employees: [e] });
  const { rebased, conflicts } = rebaseOnConflict({
    preSave: wrap(base),
    toSave: wrap(mine),
    fresh: wrap(theirs),
    strip: (s) => s,
  });
  const employee = (rebased.employees || []).find((e) => e && e.id === theirs.id) || theirs;
  return {
    employee,
    conflicts: conflicts.map((c) => ({
      ...describeConflict(c, wrap(theirs)),
      employeeRecord: true,
    })),
  };
}
